// Auth types from kit — import for local use + re-export for consumers
import type { User, AllowedEmail, AuthMethod, AuthConfig } from 'auth-sso-kit/client';
export type { User, AllowedEmail, AuthMethod, AuthConfig };

export type ResourceType = 'Human' | 'Organisational' | 'Tangible' | 'Intangible';

export interface Resource {
  id: number;
  title: string;
  description: string;
  type: ResourceType;
  function: string;
  createdBy?: number;
  createdAt: string;
}

export type CapabilityNature =
  | 'Assess'
  | 'Decide/Design'
  | 'Deliver/Execute'
  | 'Communicate/Report'
  | 'Relate'
  | 'Improve';

export type CapabilityDomain =
  | 'Participant'
  | 'Mandant'
  | 'Animateur'
  | 'Organisation'
  | 'External Environment';

export interface Capability {
  id: number;
  title: string;
  description: string;
  nature: CapabilityNature;
  domain: CapabilityDomain;
}

export interface Outcome {
  id: number;
  title: string;
  description: string;
  motivated_by: string;
  motivated_for: string;
  tags: string[];
}

export interface ResourceCapabilityLink {
  id: number;
  resourceId: number;
  capabilityId: number;
  userId: number;
  resource?: Resource;
  user?: User;
}

export type CriticalityScore = 0.00 | 0.25 | 0.50 | 0.75 | 1.00;

export interface CriticalityRating {
  id: number;
  resourceId: number;
  capabilityId: number;
  userId: number;
  score: CriticalityScore;
}

export type MaturityScore = 1 | 2 | 3 | 4 | 5;

export interface MaturityRating {
  id: number;
  resourceId: number;
  userId: number;
  score: MaturityScore;
}

export interface Refinement {
  id: number;
  resourceId: number;
  capabilityId: number;
  userId: number;
  text: string;
  user?: User;
}

export type VRIOScore = 1 | 2 | 3 | 4 | 5;

export interface VRIORating {
  id: number;
  resourceId: number;
  userId: number;
  inimitability: VRIOScore | null;
  durability: VRIOScore | null;
  appropriability: VRIOScore | null;
  substitutability: VRIOScore | null;
  competitiveSuperiority: VRIOScore | null;
}

export interface AggregateScore {
  median: number;
  min: number;
  max: number;
  spread: number;
  flagged: boolean;
  scores: number[];
}

export interface AggregateCriticality {
  resourceId: number;
  capabilityId: number;
  aggregate: AggregateScore;
  inShortlist: boolean;
  contributingUsers: User[];
}

export interface AggregateVRIO {
  resourceId: number;
  inimitability: AggregateScore;
  durability: AggregateScore;
  appropriability: AggregateScore;
  substitutability: AggregateScore;
  competitiveSuperiority: AggregateScore;
  composite: number;
}

export interface AdminToggles {
  rcLinking: boolean;
  rcLinkQualification: boolean;
  rcScoring: boolean;
  rcWeightMaturityQualification: boolean;
  rcVrio: boolean;
}

export const RESOURCE_TYPES: ResourceType[] = ['Human', 'Organisational', 'Tangible', 'Intangible'];

export const CAPABILITY_NATURES: CapabilityNature[] = [
  'Assess',
  'Decide/Design',
  'Deliver/Execute',
  'Communicate/Report',
  'Relate',
  'Improve',
];

export const CAPABILITY_DOMAINS: CapabilityDomain[] = [
  'Participant',
  'Mandant',
  'Animateur',
  'Organisation',
  'External Environment',
];

/** Maps domain names to design-kit meta-tag--cat-N colour slots */
export const DOMAIN_CAT: Record<string, number> = {
  Participant: 1,
  Mandant: 2,
  Animateur: 3,
  Organisation: 4,
  'External Environment': 5,
};

export const RESOURCE_FUNCTIONS = [
  'Service Delivery',
  'Relationship & Placement Mgmt',
  'Human Resources',
  'Finance & Administration',
  'IT, Digital & Infrastructure',
  'Marketing & Communication',
  'Strategy & Governance',
  'Data Management',
  'Cross Functional',
] as const;

export type ResourceFunction = (typeof RESOURCE_FUNCTIONS)[number];

export const CRITICALITY_OPTIONS: { value: CriticalityScore; label: string; description: string }[] = [
  { value: 0.00, label: 'Not relevant', description: 'Resource not needed for this capability' },
  { value: 0.25, label: 'Supportive', description: 'If removed, could deliver same outcome with minor adjustments' },
  { value: 0.50, label: 'Important', description: 'If removed, outcome quality or efficiency noticeably degrades' },
  { value: 0.75, label: 'Essential', description: 'If removed, would need significant workaround to deliver' },
  { value: 1.00, label: 'Critical', description: 'If removed, capability effectively ceases to function' },
];

export const MATURITY_OPTIONS: { value: MaturityScore; label: string; description: string }[] = [
  { value: 1, label: 'Non-existent', description: 'Resource lacking entirely, only an idea/intention' },
  { value: 2, label: 'Nascent', description: 'Exists in early/experimental form, inconsistent or undocumented' },
  { value: 3, label: 'Established', description: 'Functional and repeatable, not yet optimized or fully embedded' },
  { value: 4, label: 'Well-developed', description: 'Reliable, documented, maintained, integrated into operations' },
  { value: 5, label: 'Highly developed', description: 'Clear strength, refined, resilient, recognized as excellent' },
];
